// Tabs
const mediaQueryDesktop = matchMedia('(min-width: 990px)');

class TabsCustom extends HTMLElement {
  /** @type {HTMLElement[]} */
  get tabs() {
    return Array.from(this.querySelectorAll('[role="tab"]'));
  }

  /** @type {HTMLElement[]} */
  get panels() {
    return Array.from(this.querySelectorAll('[role="tabpanel"]'));
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.#ensureSelection();
  }

  /**
   * Makes sure a tab is selected. If the tab marked selected by default was hidden
   * (e.g. because its content was empty and the tab was omitted from rendering),
   * falls back to selecting the first available tab so the panel area isn't blank.
   */
  #ensureSelection() {
    const tabs = this.tabs;

    if (tabs.length === 0) return;
    if (tabs.some((tab) => tab.getAttribute('aria-selected') === 'true')) return;

    this.#selectTab(tabs[0]);
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  #handleClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;

    if (!(tab instanceof HTMLElement) || !this.tabs.includes(tab)) return;

    if (mediaQueryDesktop.matches) {
      this.#selectTab(tab);
    } else {
      this.#toggleTab(tab);
    }
  };

  /**
   * Handles the keydown event, supporting roving tabindex navigation between tabs.
   * @param {KeyboardEvent} event - The event.
   */
  #handleKeyDown = (event) => {
    if (!mediaQueryDesktop.matches) return;
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

    const tabs = this.tabs;
    const currentIndex = tabs.indexOf(/** @type {HTMLElement} */ (document.activeElement));

    if (currentIndex === -1) return;

    let newIndex = currentIndex;

    if (event.key === 'ArrowRight') newIndex = (currentIndex + 1) % tabs.length;
    if (event.key === 'ArrowLeft') newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') newIndex = 0;
    if (event.key === 'End') newIndex = tabs.length - 1;

    event.preventDefault();
    tabs[newIndex].focus();
    this.#selectTab(tabs[newIndex]);
  };

  /**
   * Toggles the given tab's panel open or closed, used for the mobile/small-tablet
   * accordion layout. Opening a tab closes any other tab that's currently open, so
   * only one panel is ever expanded at a time; clicking an open tab closes it.
   * @param {HTMLElement} tab - The tab to toggle.
   */
  #toggleTab(tab) {
    const targetId = tab.getAttribute('aria-controls');
    const panel = this.panels.find((candidate) => candidate.id === targetId);

    if (!panel) return;

    const expanding = tab.getAttribute('aria-selected') !== 'true';

    if (expanding) {
      for (const otherTab of this.tabs) {
        if (otherTab !== tab) otherTab.setAttribute('aria-selected', 'false');
      }

      for (const otherPanel of this.panels) {
        if (otherPanel !== panel) otherPanel.hidden = true;
      }
    }

    tab.setAttribute('aria-selected', String(expanding));
    panel.hidden = !expanding;
  }

  /**
   * Marks the given tab as selected, shows its panel, and hides the others.
   * @param {HTMLElement} tab - The tab to select.
   */
  #selectTab(tab) {
    const targetId = tab.getAttribute('aria-controls');

    for (const otherTab of this.tabs) {
      const selected = otherTab === tab;

      otherTab.setAttribute('aria-selected', String(selected));
      otherTab.tabIndex = selected ? 0 : -1;
    }

    for (const panel of this.panels) {
      panel.hidden = panel.id !== targetId;
    }
  }
}

if (!customElements.get('tabs-custom')) {
  customElements.define('tabs-custom', TabsCustom);
}
